import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { isDailyChartResolution } from './constants';
dayjs.extend(customParseFormat);
var channelToSubscription = new Map();
export var subscribeOnStream = function subscribeOnStream(symbolInfo, resolution, onRealtimeCallback, subscribeUID, onResetCacheNeededCallback, lastDailyBar) {
  var handler = {
    id: subscribeUID,
    callback: onRealtimeCallback
  };
  var channelString = "0~".concat(symbolInfo.exchange, "~").concat(symbolInfo.name, "~").concat(symbolInfo.name, "~").concat(resolution);
  var subscriptionItem = channelToSubscription.get(channelString);

  if (subscriptionItem) {
    subscriptionItem.handlers.push(handler);
    return;
  }

  subscriptionItem = {
    subscribeUID: subscribeUID,
    resolution: resolution,
    lastDailyBar: lastDailyBar,
    handlers: [handler]
  };
  channelToSubscription.set(channelString, subscriptionItem);
};
export var handleUpdates = function handleUpdates(symbolInfo, resolution, onRealtimeCallback, subscribeUID, onResetCacheNeededCallback, quote, isHeikinAshi) {
  // Validate quote data exists and has required fields
  if (!quote) {
    console.warn('❌ No quote data provided to handleUpdates');
    return undefined;
  }

  var LastTradePrice = quote.LastTradePrice,
      ReceivedTime = quote.ReceivedTime; // Validate required fields - but be more permissive

  if (LastTradePrice === undefined || LastTradePrice === null) {
    console.warn('❌ Missing LastTradePrice in quote:', quote);
    return undefined;
  }

  if (!ReceivedTime) {
    console.warn('❌ Missing ReceivedTime in quote:', quote);
    return undefined;
  }

  var channelString = "0~".concat(symbolInfo.exchange, "~").concat(symbolInfo.name, "~").concat(symbolInfo.name, "~").concat(resolution);
  var subscriptionItem = channelToSubscription.get(channelString);

  if (subscriptionItem === undefined) {
    console.warn('❌ No subscription found for channel:', channelString);
    return undefined;
  } // Try to parse the date with error handling


  var parsedDate;

  try {
    // First try the original format that was working before
    parsedDate = dayjs(ReceivedTime, 'MM/DD/YYYY HH:mm:ss'); // Only try other formats if the original fails

    if (!parsedDate.isValid()) {
      console.log('📅 Original format failed, trying alternatives for:', ReceivedTime);
      parsedDate = dayjs(ReceivedTime, 'YYYY-MM-DD HH:mm:ss');
    }

    if (!parsedDate.isValid()) {
      parsedDate = dayjs(ReceivedTime, 'MM-DD-YYYY HH:mm:ss');
    }

    if (!parsedDate.isValid()) {
      parsedDate = dayjs(ReceivedTime);
    }

    if (!parsedDate.isValid()) {
      console.warn('❌ Unable to parse ReceivedTime:', ReceivedTime, 'for quote:', quote);
      return undefined;
    }
  } catch (error) {
    console.warn('❌ Error parsing ReceivedTime:', error, 'for ReceivedTime:', ReceivedTime);
    return undefined;
  }

  var barTime = parsedDate.format('YYYY-MM-DDTHH:mm:ss') + 'Z';
  var date = new Date(barTime); // Validate the Date object

  if (isNaN(date.getTime())) {
    console.warn('❌ Invalid date created from barTime:', barTime, 'for ReceivedTime:', ReceivedTime);
    return undefined;
  }

  var dateValue = dayjs(date); // Use only valid volume deltas to prevent NaN or incorrect accumulation when chart first opens

  var volumeDelta = Number.isFinite(quote.LastTradeVolume) && quote.LastTradeVolume >= 0 ? quote.LastTradeVolume : 0; // getBars may not have completed yet (or history empty) — seed from first live quote so realtime never stalls.

  if (!subscriptionItem.lastDailyBar || !Number.isFinite(subscriptionItem.lastDailyBar.time)) {
    subscriptionItem.lastDailyBar = {
      time: dateValue.valueOf(),
      open: LastTradePrice,
      high: LastTradePrice,
      low: LastTradePrice,
      close: LastTradePrice,
      volume: volumeDelta
    };
  }

  var latestExistingBarTime = dayjs(subscriptionItem.lastDailyBar.time);
  var time = latestExistingBarTime.unix() > dateValue.unix() ? latestExistingBarTime : dateValue;
  var lastDailyBar = subscriptionItem.lastDailyBar; // Must be strict `>`: when the bucketed quote time equals the current bar open (`>=`), every tick in that
  // period was treated as a "new" bar (especially visible on 1m Heikin Ashi).

  var isNextBar = dateValue.unix() > latestExistingBarTime.unix();
  var bar; // heikin ashi calculations

  var haOpen = (lastDailyBar.open + lastDailyBar.close) / 2;
  var haClose = (quote.LastTradePrice + quote.LastTradePrice + quote.LastTradePrice + quote.LastTradePrice) / 4;
  var haHigh = Math.max(quote.LastTradePrice, lastDailyBar.open, lastDailyBar.close);
  var haLow = Math.min(quote.LastTradePrice, lastDailyBar.open, lastDailyBar.close);
  var lastBarVolume = Number.isFinite(lastDailyBar.volume) ? lastDailyBar.volume : 0;

  if (isNextBar) {
    var daily = isDailyChartResolution(resolution);
    bar = {
      time: time.valueOf(),
      open: daily ? lastDailyBar.open : isHeikinAshi ? haOpen : LastTradePrice,
      high: daily ? lastDailyBar.high : isHeikinAshi ? haHigh : LastTradePrice,
      low: daily ? lastDailyBar.low : isHeikinAshi ? haLow : LastTradePrice,
      close: isHeikinAshi ? haClose : LastTradePrice,
      volume: daily ? lastBarVolume : volumeDelta
    };
  } else {
    bar = Object.assign({}, lastDailyBar, {
      high: Math.max(lastDailyBar.high, LastTradePrice),
      low: Math.min(lastDailyBar.low, LastTradePrice),
      close: LastTradePrice,
      time: time.valueOf(),
      volume: lastBarVolume + volumeDelta
    });
  }

  subscriptionItem.lastDailyBar = bar;
  subscriptionItem.handlers.forEach(function (handler) {
    handler.callback(bar);
  });
  return bar;
};
export var unsubscribeFromStream = function unsubscribeFromStream(subscribeUID) {
  var _iteratorNormalCompletion = true;
  var _didIteratorError = false;
  var _iteratorError = undefined;

  try {
    for (var _iterator = channelToSubscription.keys()[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
      var channelString = _step.value;
      var subscriptionItem = channelToSubscription.get(channelString);
      var handlerIndex = subscriptionItem.handlers.findIndex(function (handler) {
        return handler.id === subscribeUID;
      });

      if (handlerIndex !== -1) {
        subscriptionItem.handlers.splice(handlerIndex, 1);

        if (subscriptionItem.handlers.length === 0) {
          channelToSubscription.delete(channelString);
          break;
        }
      }
    }
  } catch (err) {
    _didIteratorError = true;
    _iteratorError = err;
  } finally {
    try {
      if (!_iteratorNormalCompletion && _iterator.return != null) {
        _iterator.return();
      }
    } finally {
      if (_didIteratorError) {
        throw _iteratorError;
      }
    }
  }
};